import { Discount } from "~/entities/orderEntities";
import { bundleInstanceIdFromDiscount } from "~/helpers/promotionHelpers";

// One row per offer on an order, with what it actually saved (from the server's discount amounts). Instances of the
// same bundle deal collapse into one row ("Burger Combo ×2").

type ItemWithDiscounts = {
  discounts: Discount[];
  children?: ItemWithDiscounts[];
};
type LineWithDiscounts = { discounts: Discount[]; item: ItemWithDiscounts };
type OrderWithDiscounts = {
  discounts: Discount[];
  lines: LineWithDiscounts[];
  promoCode?: string | null;
  unlockedPromotions?: { code: string }[] | null;
};

export interface OfferRow {
  key: string;
  label: string;
  // Null while saving, when the optimistic order has no amounts yet
  amount: number | null;
}

export const offerRows = (order: OrderWithDiscounts): OfferRow[] => {
  const rows: (OfferRow & { instances: Set<string> })[] = [];
  const byKey: Record<string, (typeof rows)[number]> = {};

  // Offers unlocked by the promo code are labelled with the code
  const codePromotions = new Set(
    order.promoCode ? (order.unlockedPromotions ?? []).map((x) => x.code) : [],
  );
  // Bundle deals read as the deal's name alone; the deal group in the cart already shows it came from the code
  const labelFor = (discount: Discount) =>
    order.promoCode &&
    codePromotions.has(discount.promotionCode) &&
    !bundleInstanceIdFromDiscount(discount)
      ? `Code ${order.promoCode} · ${discount.name}`
      : discount.name;

  const add = (discount: Discount) => {
    const key = discount.promotionCode ?? discount.id;
    let row = byKey[key];
    if (!row) {
      row = {
        key,
        label: labelFor(discount),
        amount: 0,
        instances: new Set(),
      };
      byKey[key] = row;
      rows.push(row);
    }
    const instance = bundleInstanceIdFromDiscount(discount);
    if (instance) {
      row.instances.add(instance);
    }
    row.amount =
      row.amount == null || discount.amount == null
        ? null
        : row.amount + discount.amount;
  };

  const addItem = (item: ItemWithDiscounts) => {
    item.discounts.forEach(add);
    item.children?.forEach(addItem);
  };
  for (const line of order.lines) {
    line.discounts.forEach(add);
    addItem(line.item);
  }
  order.discounts.forEach(add);

  return rows
    .filter((x) => x.amount !== 0)
    .map(({ instances, ...row }) =>
      instances.size > 1
        ? { ...row, label: `${row.label} ×${instances.size}` }
        : row,
    );
};

// What an offer saved on one line (deal records excluded), for the pills on cart lines
export const lineOfferSavings = (line: LineWithDiscounts) => {
  const savings: Record<string, { name: string; amount: number | null }> = {};
  const add = (discount: Discount) => {
    if (bundleInstanceIdFromDiscount(discount)) {
      return;
    }
    const key = discount.promotionCode ?? discount.id;
    const current = savings[key];
    savings[key] = {
      name: discount.name,
      amount:
        discount.amount == null || (current && current.amount == null)
          ? null
          : (current?.amount ?? 0) + discount.amount,
    };
  };
  line.discounts.forEach(add);
  line.item.discounts.forEach(add);
  return Object.values(savings);
};

// The offers applied to an order other than bundle deals, by promotion code, for spotting ones that stop applying
export const appliedOfferNames = (order: OrderWithDiscounts) => {
  const names: Record<string, string> = {};
  const add = (discount: Discount) => {
    if (discount.promotionCode && !bundleInstanceIdFromDiscount(discount)) {
      names[discount.promotionCode] = discount.name;
    }
  };
  const addItem = (item: ItemWithDiscounts) => {
    item.discounts.forEach(add);
    item.children?.forEach(addItem);
  };
  for (const line of order.lines) {
    line.discounts.forEach(add);
    addItem(line.item);
  }
  order.discounts.forEach(add);
  return names;
};
